import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { extendObservable, computed } from 'mobx'
import PropTypes from 'prop-types'
import intersection from 'lodash/intersection'

import Button from '@components/Button'
import { showDialog, isCancellationError } from '@components/Dialog'
import { Dropdown } from '@components/Autocomplete'
import Radio from '@components/Radio'
import { eplApi } from '@atheneum/api'
import { isCIDProject } from '@atheneum/api/dist/lib'

class BatchActions extends Component {
  static propTypes = {
    eplStore: PropTypes.object,
    segmentStore: PropTypes.object,
    growlStore: PropTypes.object,
    staticData: PropTypes.object,
    project: PropTypes.object,
    capiProject: PropTypes.object,
    projectStore: PropTypes.object,
  }

  constructor (props) {
    super(props)

    extendObservable(this, {
      selectedRadioOption: 'status',
      selectedStatusOption: null,
      selectedSegmentOption: null,
      selectedEpls: computed(() =>
        this.props.eplStore.eplModels.filter(e => e.batchChecked)
      )
    })
  }

  getStatusOptions = () => {
    const eplStatuses = this.props.eplStore.eplStatuses
    const { EPL_STATUSES, COMPLIANCE_TYPES } = this.props.staticData.CONST
    const project = this.props.projectStore.project

    // These statuses require special entries to set their status
    // These cannot be added for multiple EPLs at once
    const disallowBatchChangeToStatuses = [
      EPL_STATUSES.NOT_INTERVIEWED, // requires interview canceled date
      EPL_STATUSES.SCHEDULED, // requires schedule,
      EPL_STATUSES.COMPLIANCE_AUDIT // automated process when selecting status SUBMITTED
    ]

    if(this.props.capiProject || isCIDProject(project,this.props.staticData.CONST)) {
      return []
    }
    if(project.complianceRequired && project.complianceTypeId === COMPLIANCE_TYPES.EY){

      disallowBatchChangeToStatuses.push(EPL_STATUSES.SUBMITTED) // requires compliance questions to be answered
    }

    let possibleEplStatuses = this.selectedEpls.map(epl => {
      const status = eplStatuses.find(s => s.id === epl.epl.eplStatusId)
      return status.eplStatusPossibleChanges.reduce((acc, status) => {
        if (
          disallowBatchChangeToStatuses.includes(status.eplPossibleStatusId)
        ) {
          return acc
        }
        acc.push(status.eplPossibleStatusId)
        return acc
      }, [])
    })

    possibleEplStatuses = intersection.apply(null, possibleEplStatuses)

    const possibleStatuses = eplStatuses.filter(status =>
      possibleEplStatuses.includes(status.id)
    )

    return possibleStatuses
  }

  handleDialogError = error => {
    if (isCancellationError(error)) return
    const { growlStore } = this.props
    growlStore.showError(error)
  }

  onSubmit = () => {
    const { INTERVIEWED, SUBMITTED, COMPLIANCE_AUDIT } = this.props.staticData.CONST.EPL_STATUSES
    const { growlStore, projectStore, modalStore } = this.props
    const { project } = projectStore
    if (this.selectedEpls.length < 1) {
      return showDialog({
        title: 'Error',
        text: 'Please select epls to update!',
        type: 'error'
      }).catch(err => isCancellationError(err))
    }

    switch (this.selectedRadioOption) {
      case 'status':
        if (!this.selectedStatusOption) {
          return showDialog({
            title: 'Error',
            text: 'Please select status to assign to!',
            type: 'error'
          }).catch(err => isCancellationError(err))
        }

        if(!project.projectCategoryId && (this.selectedStatusOption.id === SUBMITTED || this.selectedStatusOption.id === COMPLIANCE_AUDIT)){
          this.props.modalStore.show({
            type: 'PROJECT_CATEGORY_FORM',
            props: {
              title: 'Set the project category',
              projectStore,
              modalStore,
              actionAfterSubmit: () => {
                this.onSubmit()
              }
            }
          })

          return growlStore.showError(`Set the project category in the project details.
            If you don’t have project management permission, please contact your team lead or account manager.`
            , 'Project requires category')
        }

        return showDialog({
          title: 'Batch EPL status update',
          text:
            this.selectedStatusOption.id === INTERVIEWED
              ? 'This will set the delivery date as per the original schedule for all selected experts. Continue?'
              : 'Are you sure you want to update status for selected EPLs?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes'
        })
          .then(() => this.submitBatchActions(this.selectedEpls))
          .catch(this.handleDialogError)

      case 'segment':
        if (!this.selectedSegmentOption) {
          return showDialog({
            title: 'Error',
            text: 'Please select segment to assign to!',
            type: 'error'
          }).catch(err => isCancellationError(err))
        }

        return showDialog({
          title: 'Batch EPL segment update',
          text: 'Are you sure you want to update segment for selected EPLs?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes'
        })
          .then(() => this.submitBatchActions(this.selectedEpls))
          .catch(this.handleDialogError)

      default:
        break
    }
  }

  submitBatchActions = async selectedEpls => {
    const { eplStore, growlStore, project, staticData, projectStore } = this.props
    const { externalComplianceRequired } = projectStore.project.office.account
    const { complianceRequired, complianceTypeId } = projectStore.project
    const { EPL_STATUSES, COMPLIANCE_TYPES } = staticData.CONST

    try {
      const eplIds = selectedEpls.map(e => e.id)

      if (this.selectedRadioOption === 'segment') {
        const segmentId = this.selectedSegmentOption.id
        await eplApi.setSegmentForEpls(segmentId, eplIds)
      } else {
        let eplStatusId = this.selectedStatusOption.id

        if (
          externalComplianceRequired
          && complianceRequired
          && eplStatusId === EPL_STATUSES.SUBMITTED
          && complianceTypeId !== COMPLIANCE_TYPES.CID
          ) {
            eplStatusId = EPL_STATUSES.COMPLIANCE_AUDIT
        }
        await eplApi.setStatusForEpls(eplStatusId, eplIds)
      }

      eplStore.getList(project.id)
      eplStore.getFilterOptions(project.id)
      growlStore.showSuccess('EPLs successfully updated.')
    } catch (error) {
      growlStore.showError(error, 'One or more EPLs failed to update!')
    }

    this.reset()
  }

  onRadioChange (selectedRadioOption) {
    if (this.selectedRadioOption !== selectedRadioOption) {
      this.selectedRadioOption = selectedRadioOption
      this.reset()
    }
  }

  reset () {
    this.selectedSegmentOption = null
    this.selectedStatusOption = null
  }

  render () {
    if (!this.props.eplStore.doShowBatchActions) return null

    const statusOptions = this.getStatusOptions()
    const segmentOptions = this.props.segmentStore.segments

    const statusPlaceholder =
      statusOptions.length < 1
        ? 'No available options...'
        : 'Select status to assign...'

    return (
      <div className={'batch-actions'}>
        <div className='batch-actions__action-wrapper'>
          <span
            className='clickable'
            onClick={() => this.onRadioChange('status')}
          >
            <Radio checked={this.selectedRadioOption === 'status'} />
            <span className='batch-actions__action-wrapper__label'>
              Assign to status
            </span>
          </span>

          <Dropdown
            disabled={
              !this.selectedEpls.length ||
              this.selectedRadioOption !== 'status' ||
              !statusOptions.length
            }
            onChange={option => {
              this.selectedStatusOption = option
            }}
            options={statusOptions}
            itemToString={item => item && item.name}
            selectedItem={this.selectedStatusOption}
            placeholder={statusPlaceholder}
          />
        </div>
        <div className='batch-actions__action-wrapper'>
          <span
            className='clickable'
            onClick={() => this.onRadioChange('segment')}
          >
            <Radio checked={this.selectedRadioOption === 'segment'} />
            <span className='batch-actions__action-wrapper__label'>
              Assign to segment
            </span>
          </span>

          <Dropdown
            disabled={
              !this.selectedEpls.length ||
              this.selectedRadioOption !== 'segment'
            }
            onChange={option => {
              this.selectedSegmentOption = option
            }}
            options={segmentOptions}
            itemToString={item => item && item.name}
            selectedItem={this.selectedSegmentOption}
            placeholder='Select segment to assign...'
          />
        </div>
        <Button className='m-t-md' primary onClick={this.onSubmit}>
          Apply
        </Button>
      </div>
    )
  }
}

export default inject(
  'eplStore',
  'staticData',
  'segmentStore',
  'growlStore',
  'modalStore',
)(observer(BatchActions))
